import { z } from 'zod';

import { BoListQueryBase } from '@Common/types/bo/bo_list_query';
import { buildZodBoListQueryParsed } from '@Common/types/bo/utils/build_zod_bo_list_query';
import { ZodId } from '@Common/types/request';
import { createZodArrayable } from '@Common/types/zod/zod-arrayable';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { ZodNumberQuery } from '@Common/types/zod/zod-number-query';
import { createZodParamsValidator } from '@Common/types/zod/zod-params';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export const BOLogResourceValues = [
  'task',
  'instrument',
  'list',
  'user',
  'transfer',
  'settlement',
  'corporate_action',
  'marketing_modal',
  'b2b_company',
  'info_banner',
  'savings_funds_request',
  'b2b_campaign',
  'b2b_employee',
  'b2b_contract',
  'b2b_info_banner',
  'od_order',
  'marketing_market_update',
  'wm_company',
  'pea_transfer',
  'b2b_contract_fee_charge',
  'b2b_admin',
  'co_payment',
  'b2b_recurring_investment',
  'banned_device',
  'investment_plan',
  'investment_account',
  'backoffice_uploaded_file',
  'bo_user',
  'b2b_individual_operation',
  'co_payment_refund',
  'b2b_investor_profile',
] as const;
export const ZodBOLogResource = z.enum(BOLogResourceValues);
export type BOLogResource = z.TypeOf<typeof ZodBOLogResource>;

export const BOLogActionValues = [
  'assign',
  'create',
  'resolve',
  'reject',
  'archive',
  'unarchive',
  'edit',
  'info',
  'search',
  'publish',
  'unpublish',
  'link',
  'unlink',
  'close_account_by_user',
  'close_account',
  'cancel',
  'reopen',
  'plan',
  'execute',
  'email_change',
  'phone_number_change',
  'restriction_added',
  'restriction_removed',
  'restriction_edited',
  'remove_orders',
  'er_eligibility_changed',
  'ban',
  'unban',
  'delete',
  'api_key_added',
  'api_key_removed',
  'confidential_access_granted',
  'confidential_access_revoked',
  'closed_employees_access_granted',
  'closed_employees_access_revoked',
] as const;
export type BOLogAction = (typeof BOLogActionValues)[number];

export const BOLogIdFieldMap: Record<BOLogResource, string> = {
  task: 'task_id',
  instrument: 'instrument_id',
  list: 'list_id',
  user: 'user_id',
  transfer: 'mdlr_payment_id',
  settlement: 'settlement_id',
  corporate_action: 'shares_ca_id',
  marketing_modal: 'marketing_modal_id',
  b2b_company: 'b2b_company_id',
  info_banner: 'info_banner_id',
  savings_funds_request: 'savings_funds_request_id',
  b2b_campaign: 'b2b_campaign_id',
  b2b_employee: 'b2b_employee_id',
  b2b_contract: 'b2b_contract_id',
  b2b_info_banner: 'b2b_info_banner_id',
  od_order: 'od_order_id',
  marketing_market_update: 'marketing_market_update_id',
  wm_company: 'wm_company_id',
  pea_transfer: 'investment_account_id',
  b2b_contract_fee_charge: 'b2b_contract_fee_charge_id',
  b2b_admin: 'b2b_admin_id',
  co_payment: 'co_payment_id',
  b2b_recurring_investment: 'b2b_recurring_investment_id',
  banned_device: 'banned_device_id',
  investment_plan: 'investment_plan_id',
  investment_account: 'investment_account_id',
  backoffice_uploaded_file: 'backoffice_uploaded_file_id',
  bo_user: 'bo_user_id',
  b2b_individual_operation: 'b2b_individual_operation_id',
  co_payment_refund: 'co_payment_refund_id',
  b2b_investor_profile: 'b2b_fully_managed_allocation_template_id',
};

export type BOLogPayload =
  | {
      task_id: number;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'task';
      extra_info: string | null;
    }
  | {
      instrument_id: number;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'instrument';
      extra_info: string | null;
    }
  | {
      list_id: number;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'list';
      extra_info: string | null;
    }
  | {
      user_id: number;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'user';
      extra_info: string | null;
    }
  | {
      mdlr_payment_id: number;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'transfer';
      extra_info: string | null;
    }
  | {
      settlement_id: number;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'settlement';
      extra_info: string | null;
    }
  | {
      shares_ca_id: number;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'corporate_action';
      extra_info: string | null;
    }
  | {
      marketing_modal_id: number;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'marketing_modal';
      extra_info: string | null;
    }
  | {
      b2b_company_id: number;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'b2b_company';
      extra_info: string | null;
    }
  | {
      info_banner_id: number;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'info_banner';
      extra_info: string | null;
    }
  | {
      savings_funds_request_id: number;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'savings_funds_request';
      extra_info: string | null;
    }
  | {
      b2b_campaign_id: number;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'b2b_campaign';
      extra_info: string | null;
    }
  | {
      b2b_employee_id: number;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'b2b_employee';
      extra_info: string | null;
    }
  | {
      b2b_contract_id: number;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'b2b_contract';
      extra_info: string | null;
    }
  | {
      b2b_contract_fee_charge_id: number;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'b2b_contract_fee_charge';
      extra_info: string | null;
    }
  | {
      b2b_info_banner_id: number;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'b2b_info_banner';
      extra_info: string | null;
    }
  | {
      od_order_id: number;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'od_order';
      extra_info: string | null;
    }
  | {
      marketing_market_update_id: number;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'marketing_market_update';
      extra_info: string | null;
    }
  | {
      wm_company_id: number;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'wm_company';
      extra_info: string | null;
    }
  | {
      investment_account_id: number;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'pea_transfer';
      extra_info: string | null;
    }
  | {
      b2b_admin_id: number;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'b2b_admin';
      extra_info: string | null;
    }
  | {
      co_payment_id: number;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'co_payment';
      extra_info: string | null;
    }
  | {
      b2b_recurring_investment_id: number;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'b2b_recurring_investment';
      extra_info: string | null;
    }
  | {
      banned_device_id: string;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'banned_device';
      extra_info: string | null;
    }
  | {
      investment_plan_id: number;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'investment_plan';
      extra_info: string | null;
    }
  | {
      investment_account_id: number;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'investment_account';
      extra_info: string | null;
    }
  | {
      backoffice_uploaded_file_id: number;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'backoffice_uploaded_file';
      extra_info: string | null;
    }
  | {
      bo_user_id: number;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'bo_user';
      extra_info: string | null;
    }
  | {
      b2b_individual_operation_id: number;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'b2b_individual_operation';
      extra_info: string | null;
    }
  | {
      co_payment_refund_id: number;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'co_payment_refund';
      extra_info: string | null;
    }
  | {
      b2b_fully_managed_allocation_template_id: number;
      action: BOLogAction;
      author_email: string;
      author_id: number | null;
      resource: 'b2b_investor_profile';
      extra_info: string | null;
    };

export const ZodBOLogResponse = z.object({
  id: ZodId,
  instrument_id: ZodId.nullable(),
  task_id: ZodId.nullable(),
  user_id: ZodId.nullable(),
  list_id: ZodId.nullable(),
  action: z.enum(BOLogActionValues),
  author_email: ZodTrimmedString,
  author_id: ZodId.nullable(),
  resource: ZodBOLogResource,
  extra_info: ZodTrimmedString.nullable(),
  created: z.date(),
  shares_ca_id: ZodId.nullable(),
  mdlr_payment_id: ZodId.nullable(),
  marketing_modal_id: ZodId.nullable(),
  b2b_company_id: ZodId.nullable(),
  info_banner_id: ZodId.nullable(),
  savings_funds_request_id: ZodId.nullable(),
  b2b_campaign_id: ZodId.nullable(),
  b2b_employee_id: ZodId.nullable(),
  wm_company_id: ZodId.nullable(),
  investment_account_id: ZodId.nullable(),
  b2b_contract_fee_charge_id: ZodId.nullable(),
  b2b_admin_id: ZodId.nullable(),
  marketing_market_update_id: ZodId.nullable(),
  b2b_recurring_investment_id: ZodId.nullable(),
  banned_device_id: ZodTrimmedString.nullable(),
  od_order_id: ZodId.nullable(),
  investment_plan_id: ZodId.nullable(),
  backoffice_uploaded_file_id: ZodId.nullable(),
  bo_user_id: ZodId.nullable(),
  b2b_individual_operation_id: ZodId.nullable(),
  co_payment_id: ZodId.nullable(),
  co_payment_refund_id: ZodId.nullable(),
  b2b_fully_managed_allocation_template_id: ZodId.nullable(),
});
export type BOLogResponse = z.TypeOf<typeof ZodBOLogResponse>;

export const ZodPostBOLogsResponse = z.object({
  success: z.boolean(),
});
export type PostBOLogsResponse = z.TypeOf<typeof ZodPostBOLogsResponse>;

const ZodGetBOLogsResponse = z.array(ZodBOLogResponse);
export type GetBOLogsResponse = z.TypeOf<typeof ZodGetBOLogsResponse>;

const ZodGetBOLogsPathParams = createZodParamsValidator(
  z.object({
    resource: ZodBOLogResource,
  }),
);
export type GetBOLogsPathParams = z.TypeOf<typeof ZodGetBOLogsPathParams>;

export const getBOLogsSortColumns: (keyof BOLogResponse)[] = [
  'id',
  'instrument_id',
  'task_id',
  'user_id',
  'list_id',
  'action',
  'author_email',
  'author_id',
  'resource',
  'extra_info',
  'created',
  'shares_ca_id',
  'mdlr_payment_id',
  'marketing_modal_id',
  'b2b_company_id',
  'info_banner_id',
  'savings_funds_request_id',
  'b2b_employee_id',
  'wm_company_id',
  'b2b_contract_fee_charge_id',
  'b2b_admin_id',
  'marketing_market_update_id',
  'b2b_recurring_investment_id',
  'banned_device_id',
];

const ZodFilters = z
  .object({
    id: createZodArrayable(ZodNumberQuery),
    task_id: createZodArrayable(ZodId),
    instrument_id: createZodArrayable(ZodId),
    user_id: createZodArrayable(ZodId),
    list_id: createZodArrayable(ZodId),
    shares_ca_id: createZodArrayable(ZodTrimmedString).or(ZodId),
    action: createZodArrayable(z.enum(BOLogActionValues)),
    mdlr_payment_id: createZodArrayable(ZodId),
    marketing_modal_id: createZodArrayable(ZodId),
    b2b_company_id: createZodArrayable(ZodId),
    info_banner_id: createZodArrayable(ZodId),
    savings_funds_request_id: createZodArrayable(ZodId),
    b2b_campaign_id: createZodArrayable(ZodId),
    b2b_employee_id: createZodArrayable(ZodId),
    b2b_contract_id: createZodArrayable(ZodId),
    wm_company_id: createZodArrayable(ZodId),
    b2b_contract_fee_charge_id: createZodArrayable(ZodId),
    b2b_admin_id: createZodArrayable(ZodId),
    marketing_market_update_id: createZodArrayable(ZodId),
    b2b_recurring_investment_id: createZodArrayable(ZodId),
    banned_device_id: createZodArrayable(ZodTrimmedString),
    od_order_id: createZodArrayable(ZodId),
    investment_plan_id: createZodArrayable(ZodId),
    investment_account_id: createZodArrayable(ZodId),
    backoffice_uploaded_file_id: createZodArrayable(ZodId),
    bo_user_id: createZodArrayable(ZodId),
    b2b_individual_operation_id: createZodArrayable(ZodId),
    co_payment_id: createZodArrayable(ZodId),
    co_payment_refund_id: createZodArrayable(ZodId),
    b2b_fully_managed_allocation_template_id: createZodArrayable(ZodId),
  })
  .partial();

const ZodGetBOLogsQuery = buildZodBoListQueryParsed(ZodFilters, getBOLogsSortColumns, {
  sort: ['id', 'DESC'],
  range: [0, 100],
});

export type GetBOLogsQuery = BoListQueryBase<
  z.TypeOf<typeof ZodFilters>,
  (typeof getBOLogsSortColumns)[number]
>;

export const getLogsResourceEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodGetBOLogsPathParams,
  queryParams: ZodGetBOLogsQuery,
  responseBody: ZodGetBOLogsResponse,
});

export type GetLogsResourceEndpoint = GetEndpointType<typeof getLogsResourceEndpoint>;
